import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Per-coil inventory (individual coil rolls tracked by batch, weight and length).
 *
 * Deliberately uncached, unlike warehouseInventoryService: a coil's
 * remaining_weight_kg / available_weight_kg change every time somebody picks,
 * loads or adjusts one, and a stale list would offer a coil that another order
 * already consumed. Coil lists are small (one warehouse × one product size), so
 * a fresh read is cheap.
 */

// Axios instance for the coil API
const coilAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
coilAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
coilAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/coils';

const coilService = {
  /**
   * 1. List coils.
   *
   * @param {Object} params
   * @param {number} [params.warehouse_id]     Single warehouse (the global SideNav filter
   *                                           adds warehouse_ids on top via the interceptor)
   * @param {number} [params.product_size_id]  Restrict to one product size
   * @param {string} [params.colour]           Exact colour match
   * @param {string} [params.batch]            Batch number search
   * @param {boolean} [params.available_only]  Only coils with available weight left
   * @param {boolean} [params.include_inactive] Include exhausted / written-off coils
   * @param {number} [params.skip]
   * @param {number} [params.limit]
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   *          data = { items, total, skip, limit, total_remaining_kg, total_available_kg }
   */
  async getCoils(params = {}) {
    try {
      // Pass params through the axios config (not a hand-built URL) so the
      // applyWarehouseFilter interceptor can inject the global warehouse_ids and
      // warehouseParamsSerializer emits ?warehouse_ids=1&warehouse_ids=3.
      const requestParams = {};
      if (params.warehouse_id !== undefined) requestParams.warehouse_id = params.warehouse_id;
      if (params.product_size_id !== undefined) requestParams.product_size_id = params.product_size_id;
      if (params.colour) requestParams.colour = params.colour;
      if (params.batch) requestParams.batch = params.batch;
      if (params.available_only !== undefined) requestParams.available_only = params.available_only;
      if (params.include_inactive !== undefined) requestParams.include_inactive = params.include_inactive;
      if (params.skip !== undefined) requestParams.skip = params.skip;
      if (params.limit !== undefined) requestParams.limit = params.limit;

      const response = await coilAPI.get(BASE_URL, { params: requestParams });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 2. Get a single coil by id
  async getCoilById(id) {
    try {
      const response = await coilAPI.get(`${BASE_URL}/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 3. Create a coil (manual entry, or at purchase receipt)
  // Payload: { warehouse_id, product_size_id, batch_number, supplier_batch_ref?, colour?,
  //            received_date, weight_kg, length_m, remark? }
  async createCoil(payload) {
    try {
      const response = await coilAPI.post(BASE_URL, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 4. Update a coil's descriptive fields only — weight and length are never edited here,
  // they move through adjustCoilWeight so the change is auditable.
  // Payload: { batch_number?, supplier_batch_ref?, colour?, received_date?, remark? }
  async updateCoil(id, payload) {
    try {
      const response = await coilAPI.put(`${BASE_URL}/${id}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 5. Correct a coil's remaining weight (stock-take / weighbridge correction)
  // Payload: { remaining_weight_kg, note? } — whole kg (database constraint)
  async adjustCoilWeight(id, payload) {
    try {
      const response = await coilAPI.post(`${BASE_URL}/${id}/adjust-weight`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 6. Write off a coil (damaged, lost, rejected). Payload: { reason }
  async writeOffCoil(id, payload) {
    try {
      const response = await coilAPI.post(`${BASE_URL}/${id}/write-off`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * 7. Reconcile coil weights against warehouse-inventory stock.
   *
   * @param {Object} params
   * @param {number} [params.warehouse_id]
   * @param {number} [params.product_size_id]
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   *          data = [{ warehouse_id, product_size_id, product_name, warehouse_name,
   *                    stock_quantity, coil_total_kg, difference_kg, coil_count, matched }]
   */
  async getReconciliation(params = {}) {
    try {
      const requestParams = {};
      if (params.warehouse_id !== undefined) requestParams.warehouse_id = params.warehouse_id;
      if (params.product_size_id !== undefined) requestParams.product_size_id = params.product_size_id;

      const response = await coilAPI.get(`${BASE_URL}/reconciliation`, { params: requestParams });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },
};

export default coilService;
